import type { ChartConfiguration } from 'chart.js';
import type { ChartOptionsConfig } from '../../data/charts/chart-options/chart-options-storage.service';
import type { HtmlLegendItem } from '../../data/charts/chart-legend/chartjs-plugin-html-legend';

export const AUTO_RANGE: ChartOptionsConfig = { yAxisMode: 'auto-data' };

export interface ExplorerAxisMeta { label: string; unit: string }

export interface ExplorerChartDataset {
  /** Final legend text (group parts + variable). */
  label: string;
  /** Joined group label from the server (bar-chart category). */
  datasetLabel: string;
  axis?: 'primary' | 'secondary';
  dataPoints: { x: string | Date; y: number; count?: number }[];
}

export interface ExplorerChartInput {
  datasets: ExplorerChartDataset[];
  timeAgg: string;
  primary: ExplorerAxisMeta;
  /** null when there is no secondary variable (always ignored in bar/group mode). */
  secondary: ExplorerAxisMeta | null;
  ranges: { y: ChartOptionsConfig; y2: ChartOptionsConfig };
  /** Receives the legend items (HtmlLegendPlugin) — the legend is rendered as app DOM, never on the canvas. */
  onLegendChange?: (items: HtmlLegendItem[]) => void;
}

/** Manual range → min/max on the scale; auto → bounds cleared so Chart.js fits the data. */
export function applyRangeToScale(scale: Record<string, any>, cfg: ChartOptionsConfig): void {
  delete scale['min'];
  delete scale['max'];
  if (cfg.yAxisMode === 'manual' && cfg.yMin != null && cfg.yMax != null) {
    scale['min'] = cfg.yMin;
    scale['max'] = cfg.yMax;
  }
}

/** Draft inputs → a config to apply, or null while a manual range is incomplete. */
export function rangeConfigFromDraft(mode: 'auto-data' | 'manual', min: number | null, max: number | null): ChartOptionsConfig | null {
  if (mode === 'auto-data') return { yAxisMode: 'auto-data' };
  if (min == null || max == null) return null;
  return { yAxisMode: 'manual', yMin: min, yMax: max };
}

export function axisTitle(meta: ExplorerAxisMeta, withUnit: boolean): string {
  return withUnit && meta.unit ? `${meta.label} (${meta.unit})` : meta.label;
}

/** Axis ranges are tied to a variable: a different (or removed) variable starts that axis back at auto. */
export function resetRangesOnVariableChange(
  prev: { primary: string | null; secondary: string | null },
  next: { primary: string | null; secondary: string | null },
): { y: boolean; y2: boolean } {
  return { y: prev.primary !== next.primary, y2: prev.secondary !== next.secondary };
}

/**
 * The datasets the chart actually draws: a secondary variable's datasets only count while a
 * secondary axis is active (never in group/bar mode, where every dataset is a category).
 * Shared by the chart builder and the CSV export so both always describe the same series.
 */
export function usableDatasets(
  datasets: ExplorerChartDataset[],
  timeAgg: string,
  secondary: ExplorerAxisMeta | null,
): ExplorerChartDataset[] {
  const dual = !!secondary && timeAgg !== 'group';
  return timeAgg === 'group' || dual ? datasets : datasets.filter((d) => d.axis !== 'secondary');
}

export function buildExplorerChartConfig(input: ExplorerChartInput): ChartConfiguration {
  const grouped = input.timeAgg === 'group';
  const dual = !!input.secondary && !grouped;

  const yScale: Record<string, any> = { title: { display: true, text: axisTitle(input.primary, true) }, beginAtZero: true };
  applyRangeToScale(yScale, input.ranges.y);
  const scalesY: Record<string, any> = { y: yScale };
  if (dual) {
    const y2: Record<string, any> = {
      position: 'right',
      title: { display: true, text: axisTitle(input.secondary!, true) },
      grid: { drawOnChartArea: false },
    };
    applyRangeToScale(y2, input.ranges.y2);
    scalesY['y2'] = y2;
  }

  const base = { responsive: true, maintainAspectRatio: false };
  const legendPlugins = {
    legend: { display: false },
    ...(input.onLegendChange ? { htmlLegend: { onLabelsChange: input.onLegendChange } } : {}),
  };

  if (grouped) {
    return {
      type: 'bar',
      data: {
        labels: input.datasets.map((d) => d.datasetLabel),
        datasets: [{ label: input.primary.label, data: input.datasets.map((d) => d.dataPoints[0]?.y || 0), borderWidth: 1 }],
      },
      options: {
        ...base,
        plugins: { ...legendPlugins },
        scales: { x: { title: { display: true, text: 'Group' } }, ...scalesY },
      },
    } as ChartConfiguration;
  }

  const usable = usableDatasets(input.datasets, input.timeAgg, input.secondary);

  if (input.timeAgg === 'none') {
    return {
      type: 'scatter',
      data: {
        datasets: usable.map((d) => ({
          label: d.label,
          data: d.dataPoints.map((p) => ({ x: new Date(p.x).getTime(), y: p.y })),
          borderWidth: 1,
          ...(d.axis === 'secondary' ? { yAxisID: 'y2', pointStyle: 'triangle' } : {}),
        })),
      },
      options: {
        ...base,
        plugins: {
          ...legendPlugins,
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const date = new Date(context.parsed.x);
                return `${context.dataset.label}: ${context.parsed.y} (${date.toLocaleDateString()})`;
              },
            },
          },
        },
        scales: { x: { type: 'time', time: { unit: 'day' }, title: { display: true, text: 'Date' } }, ...scalesY },
      },
    } as ChartConfiguration;
  }

  const labels = Array.from(new Set(usable.flatMap((d) => d.dataPoints.map((p) => p.x as string)))).sort();
  return {
    type: 'line',
    data: {
      labels,
      datasets: usable.map((d) => {
        const byX = new Map(d.dataPoints.map((p) => [p.x as string, p.y]));
        return {
          label: d.label,
          data: labels.map((l) => byX.get(l) ?? null),
          borderWidth: 2, tension: 0.3, fill: false,
          ...(d.axis === 'secondary' ? { yAxisID: 'y2', borderDash: [6, 4] } : {}),
        };
      }),
    },
    options: {
      ...base,
      plugins: { ...legendPlugins },
      scales: { x: { title: { display: true, text: `Time (${input.timeAgg})` } }, ...scalesY },
    },
  } as ChartConfiguration;
}

export interface ExplorerCsvInput {
  datasets: ExplorerChartDataset[];
  timeAgg: string;
  primary: ExplorerAxisMeta;
  secondary: ExplorerAxisMeta | null;
  /** Indexes (within `usableDatasets`) of series hidden in the legend — left out, like in the chart. */
  hiddenIndexes: number[];
}

const csvField = (value: unknown): string => {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const csvRow = (cells: unknown[]) => cells.map(csvField).join(',');

/**
 * Long-format CSV of what the chart shows — one row per point, each series carrying its own x
 * values (series do not share a time axis):
 *   raw / time aggregation: `series,variable,unit,axis,x,y[,count]` (raw x as ISO-8601)
 *   group (bar) mode:       `group,variable,unit,value,count`
 */
export function buildExplorerCsv(input: ExplorerCsvInput): string {
  const datasets = usableDatasets(input.datasets, input.timeAgg, input.secondary);

  if (input.timeAgg === 'group') {
    const rows = datasets.flatMap((d) =>
      d.dataPoints.map((p) => csvRow([d.datasetLabel, input.primary.label, input.primary.unit, p.y, p.count])));
    return [csvRow(['group', 'variable', 'unit', 'value', 'count']), ...rows].join('\n');
  }

  const hidden = new Set(input.hiddenIndexes);
  const visible = datasets.filter((_, i) => !hidden.has(i));
  const withCount = visible.some((d) => d.dataPoints.some((p) => p.count !== undefined));

  const xCell = (x: string | Date): string => {
    if (input.timeAgg !== 'none') return String(x);
    const t = new Date(x);
    return Number.isNaN(t.getTime()) ? String(x) : t.toISOString();
  };

  const header = ['series', 'variable', 'unit', 'axis', 'x', 'y', ...(withCount ? ['count'] : [])];
  const rows = visible.flatMap((d) => {
    const meta = d.axis === 'secondary' && input.secondary ? input.secondary : input.primary;
    return d.dataPoints.map((p) =>
      csvRow([d.label, meta.label, meta.unit, d.axis ?? 'primary', xCell(p.x), p.y, ...(withCount ? [p.count] : [])]));
  });
  return [csvRow(header), ...rows].join('\n');
}
